import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Eye, FileDown, Loader2, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import api from '../api';

const BADGE = {
  overdue: ['Overdue', 'bg-red-100 text-red-700', AlertTriangle],
  waiting: ['Waiting', 'bg-amber-100 text-amber-800', Clock],
  viewed: ['Opened by doctor', 'bg-blue-100 text-blue-700', Eye],
  signed: ['Signed', 'bg-green-100 text-green-700', CheckCircle2],
  cancelled: ['Withdrawn', 'bg-gray-100 text-gray-600', Clock],
};
const FILTERS = [['open', 'Still waiting'], ['overdue', 'Overdue'], ['signed', 'Signed'], ['all', 'All']];
const Badge = ({ s }) => { const [l, c, I] = BADGE[s] || BADGE.waiting; return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c}`}><I className="w-3 h-3" />{l}</span>; };

const Row = ({ r, busy, act, open }) => (
  <div className="border-t p-4 grid gap-3 sm:grid-cols-[1.3fr_1.2fr_1.2fr_1fr_1fr_auto] sm:items-center hover:bg-gray-50" data-testid={`order-sign-row-${r.id}`}>
    <div><p className="font-medium text-gray-900">{r.patientName}</p><p className="text-sm text-gray-600 sm:hidden">{r.orderType}</p></div>
    <p className="hidden sm:block text-sm">{r.orderType}</p>
    <div className="text-sm">{r.physicianName}<div className="text-xs text-gray-500">{r.channels.join(' + ')}</div></div>
    <div className="text-sm">Sent {new Date(r.createdAt).toLocaleDateString()}<div className="text-xs text-gray-500">{r.daysOut} day{r.daysOut === 1 ? '' : 's'} out
      {r.resendCount ? `, resent ${r.resendCount}x (last ${new Date(r.lastSentAt).toLocaleDateString()})` : ''}</div></div>
    <div><Badge s={r.status} />{r.status === 'signed' && <div className="text-xs text-gray-500 mt-1">{r.signedName}</div>}</div>
    <div className="flex flex-wrap gap-2 sm:justify-end">
      <button onClick={() => open(r)} className="p-1.5 rounded border hover:bg-gray-200" title="View PDF" data-testid={`order-sign-pdf-${r.id}`}><FileDown className="w-4 h-4" /></button>
      {['overdue', 'waiting', 'viewed'].includes(r.status) && <>
        <button onClick={() => act(r, 'resend')} disabled={busy === r.id} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#d4af37] text-gray-900 text-xs font-semibold" data-testid={`order-sign-resend-${r.id}`}>
          {busy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Resend</button>
        <button onClick={() => window.confirm(`Mark the ${r.orderType} for ${r.patientName} as signed (received on paper or by fax)?`) && act(r, 'mark-signed')}
          className="px-3 py-1.5 rounded-full border text-xs" data-testid={`order-sign-mark-${r.id}`}>Got it signed</button>
        <button onClick={() => window.confirm(`Withdraw this ${r.orderType}? The doctor's link will stop working.`) && act(r, 'cancel')}
          className="px-3 py-1.5 rounded-full border text-xs text-gray-600" data-testid={`order-sign-cancel-${r.id}`}>Withdraw</button>
      </>}
    </div>
  </div>
);

export default function OrderSignaturesPage() {
  const [data, setData] = useState({ requests: [], counts: {} });
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => api.get('/order-sign/requests').then(r => setData(r.data)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);
  const act = async (r, what) => {
    setBusy(r.id);
    try { const res = await api.post(`/order-sign/requests/${r.id}/${what}`); toast.success(res.data.message || 'Order withdrawn'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(''); }
  };
  const open = async (r) => {
    const res = await api.get(`/order-sign/requests/${r.id}/pdf`, { responseType: 'blob' });
    window.open(URL.createObjectURL(res.data), '_blank');
  };
  const rows = data.requests.filter(r => (filter === 'all' ? true : filter === 'open' ? ['overdue', 'waiting', 'viewed'].includes(r.status) : r.status === filter));
  const c = data.counts || {};
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5" data-testid="order-signatures-page">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Order Signatures</h1>
            <p className="text-sm text-gray-600 mt-1">Orders sent to doctors. Overdue = not signed within 7 days.</p>
          </div>
          <button onClick={load} className="p-2 rounded-lg border hover:bg-gray-50" data-testid="order-sign-refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[['overdue', 'Overdue', 'border-red-300 text-red-700'], ['waiting', 'Waiting', 'border-amber-300 text-amber-800'], ['viewed', 'Opened by doctor', 'border-blue-300 text-blue-700'], ['signed', 'Signed', 'border-green-300 text-green-700']].map(([k, l, cls]) => (
            <div key={k} className={`bg-white rounded-xl border-l-4 ${cls} p-4 shadow-sm`} data-testid={`order-sign-count-${k}`}>
              <p className="text-xs uppercase tracking-wide text-gray-500">{l}</p><p className="text-3xl font-bold">{c[k] || 0}</p>
            </div>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map(([k, l]) => <button key={k} onClick={() => setFilter(k)} data-testid={`order-sign-filter-${k}`}
            className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${filter === k ? 'bg-gray-900 text-[#d4af37] border-gray-900' : 'bg-white hover:bg-gray-50'}`}>{l}</button>)}
        </div>
        <div className="bg-white rounded-xl shadow-sm">
          {loading ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div> : rows.length === 0
            ? <p className="p-10 text-center text-gray-500" data-testid="order-sign-empty">No orders here. Send one from the AI Document Generator with "Send to doctor for signature".</p>
            : <div>
                <div className="hidden sm:grid grid-cols-[1.3fr_1.2fr_1.2fr_1fr_1fr_auto] gap-3 px-4 py-2 bg-gray-50 text-xs uppercase text-gray-500"><span>Patient</span><span>Order</span><span>Doctor</span><span>Sent</span><span>Status</span><span /></div>
                {rows.map(r => <Row key={r.id} r={r} busy={busy} act={act} open={open} />)}
              </div>}
        </div>
      </div>
    </DashboardLayout>
  );
}
